/**
 * El logo: los cuatro símbolos de respuesta en cuadrícula 2×2.
 *
 * Es lo que describe `docs/marca.md` y lo que no existía en el código: la pantalla de acceso
 * enseñaba el nombre en texto y nada más. Las formas y los colores salen de `ANSWER_TOKENS`, la
 * misma fuente que usa la ficha de una respuesta, así que el logo no puede divergir de lo que el
 * alumno ve luego en su móvil.
 *
 * El círculo lleva la onda expansiva de pulsación, que es lo que convierte cuatro formas sueltas
 * en un gesto: alguien acaba de clicar.
 */

import { ANSWER_TOKENS } from '@clica/shared';

/** Las cuatro formas, cada una centrada en su cuadrante de una caja de 100. */
const CUADRANTES = [
  { cx: 27, cy: 27 },
  { cx: 73, cy: 27 },
  { cx: 27, cy: 73 },
  { cx: 73, cy: 73 },
] as const;

const R = 17;

/** Centro del círculo, que es el que lleva la onda. */
const CX = 73;
const CY = 73;

/**
 * Un arco de 180° por el lado de FUERA del logo.
 *
 * Barre desde arriba a la derecha hasta abajo a la izquierda pasando por el exterior, que es
 * justo la parte donde no hay ninguna otra forma. Un aro cerrado se leía como el borde del
 * círculo naranja en vez de como una onda, y además cruzaba por encima del cuadrado verde.
 */
function arcoExterior(radio: number): string {
  const d = radio * Math.SQRT1_2;
  return `M${CX + d} ${CY - d} A${radio} ${radio} 0 1 1 ${CX - d} ${CY + d}`;
}

/** Tres frentes: cuanto más lejos, más tenue y más fino, como se apaga una onda de verdad. */
const ONDAS = [
  { radio: R * 1.32, grosor: 2.4, opacidad: 0.75 },
  { radio: R * 1.72, grosor: 1.8, opacidad: 0.45 },
  { radio: R * 2.12, grosor: 1.3, opacidad: 0.22 },
] as const;

function forma(indice: number, cx: number, cy: number): React.ReactNode {
  const token = ANSWER_TOKENS[indice];
  if (token === undefined) return null;
  const color = token.color;

  switch (token.shape) {
    case 'diamond':
      return (
        <path
          d={`M${cx} ${cy - R} L${cx + R} ${cy} L${cx} ${cy + R} L${cx - R} ${cy} Z`}
          fill={color}
        />
      );
    case 'triangle':
      return (
        <path
          d={`M${cx} ${cy - R} L${cx + R * 0.95} ${cy + R * 0.8} L${cx - R * 0.95} ${cy + R * 0.8} Z`}
          fill={color}
        />
      );
    case 'square':
      return (
        <rect
          x={cx - R * 0.88}
          y={cy - R * 0.88}
          width={R * 1.76}
          height={R * 1.76}
          rx={3}
          fill={color}
        />
      );
    case 'circle':
      return <circle cx={cx} cy={cy} r={R * 0.92} fill={color} />;
    default:
      return null;
  }
}

export interface MarcaProps {
  readonly size?: number;
  readonly className?: string;
}

export function Marca({ size = 96, className }: MarcaProps): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="-2 -2 116 116"
      // El nombre de la aplicación va escrito al lado, así que el logo no aporta nada que leer.
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {CUADRANTES.map((q, i) => (
        <g key={i}>{forma(i, q.cx, q.cy)}</g>
      ))}
      {/*
        La onda expansiva sobre el círculo: alguien acaba de pulsarlo.

        Van DOS arcos abiertos y no un aro cerrado. Un círculo completo alrededor del círculo
        naranja se lee como su borde, no como una onda, y además le pone un quinto color encima
        justo a la ficha D, que es la que menos contraste tiene. Abriendo el trazo por arriba a
        la izquierda —de donde vendría el dedo— la forma se lee como movimiento.
      */}
      <g fill="none" stroke="currentColor" strokeLinecap="round">
        {ONDAS.map(({ radio, grosor, opacidad }) => (
          <path key={radio} d={arcoExterior(radio)} strokeWidth={grosor} opacity={opacidad} />
        ))}
      </g>
    </svg>
  );
}
